import { Base } from '../../abstract/base.model';
import { isRecord } from '../utils/is-record';
import { nullableBoolean, nullableString, nullableStringArray } from '../utils/wire-value-utils';
import { EpaChemicalCustomName } from './epa-chemical-custom-name.model';
import { EpaChemicalLabel } from './epa-chemical-label.model';
import { EpaChemicalStatus } from './epa-chemical-status.model';

/**
 * An EPA-synced chemical, built from `/admin/epa-chemicals` JSON (hub `admin-epa-chemicals-controller.ts`).
 * Only `sdsUrl`, `adminNotes` and `isHidden` are writable; every other field is EPA-managed. The detail route
 * adds `customNames` and `labels`; both list and detail add the computed `displayName`.
 *
 * Read-modify-write rule: fields the hub didn't send stay `undefined` (an explicit `null` stays `null`).
 */
export class EpaChemical extends Base {
  // Writable via PUT /admin/epa-chemicals/:id
  sdsUrl?: string | null;
  adminNotes?: string | null;
  isHidden?: boolean | null;

  // EPA-managed, read-only
  name?: string | null;
  displayName?: string | null;
  epaNumber?: string | null;
  manufacturer?: string | null;
  activeIngredient?: string | null;
  signalWord?: string | null;
  category?: string | null;
  formulation?: string | null;
  rei?: string | null;
  phi?: string | null;
  defaultRate?: string | null;
  targetPests?: string[] | null;
  crops?: string[] | null;
  source?: string | null;
  registrationType?: string | null;
  companyNumber?: string | null;
  firstRegistrationDate?: string | null;
  status?: EpaChemicalStatus | null;
  statusDescription?: string | null;
  statusGroup?: string | null;
  statusDate?: string | null;
  useType?: string | null;
  rupFlag?: boolean | null;
  rupReason?: string | null;
  pesticideType?: string | null;
  physicalForm?: string | null;
  physicalFormName?: string | null;
  sites?: string | null;
  pests?: string | null;
  usePattern?: string | null;
  transferHistory?: string | null;
  epaAltBrandNames?: string | null;
  meTooFlag?: boolean | null;
  meTooRefs?: string | null;
  maxLabelDate?: string | null;
  labelDates?: string | null;
  labelNames?: string | null;
  pmEmail?: string | null;
  epaTeam?: string | null;
  epaPpisSyncedAt?: string | null;
  epaPplsSyncedAt?: string | null;

  /** Only on the detail routes; `undefined` on list rows. */
  customNames?: EpaChemicalCustomName[];
  /** Only on the detail routes; `undefined` on list rows. */
  labels?: EpaChemicalLabel[];

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.sdsUrl = nullableString(source['sdsUrl']);
    this.adminNotes = nullableString(source['adminNotes']);
    this.isHidden = nullableBoolean(source['isHidden']);
    this.readIdentity(source);
    this.readRegistration(source);
    this.readPpls(source);
    const customNames = source['customNames'];
    const labels = source['labels'];
    this.customNames = Array.isArray(customNames) ? customNames.map(item => new EpaChemicalCustomName(item)) : undefined;
    this.labels = Array.isArray(labels) ? labels.map(item => new EpaChemicalLabel(item)) : undefined;
  }

  private readIdentity(source: Record<string, unknown>): void {
    this.name = nullableString(source['name']);
    this.displayName = nullableString(source['displayName']);
    this.epaNumber = nullableString(source['epaNumber']);
    this.manufacturer = nullableString(source['manufacturer']);
    this.activeIngredient = nullableString(source['activeIngredient']);
    this.signalWord = nullableString(source['signalWord']);
    this.category = nullableString(source['category']);
    this.formulation = nullableString(source['formulation']);
    this.rei = nullableString(source['rei']);
    this.phi = nullableString(source['phi']);
    this.defaultRate = nullableString(source['defaultRate']);
    this.targetPests = nullableStringArray(source['targetPests']);
    this.crops = nullableStringArray(source['crops']);
    this.source = nullableString(source['source']);
  }

  private readRegistration(source: Record<string, unknown>): void {
    this.registrationType = nullableString(source['registrationType']);
    this.companyNumber = nullableString(source['companyNumber']);
    this.firstRegistrationDate = nullableString(source['firstRegistrationDate']);
    this.status = nullableString(source['status']) as EpaChemicalStatus | null | undefined;
    this.statusDescription = nullableString(source['statusDescription']);
    this.statusGroup = nullableString(source['statusGroup']);
    this.statusDate = nullableString(source['statusDate']);
    this.useType = nullableString(source['useType']);
    this.rupFlag = nullableBoolean(source['rupFlag']);
    this.rupReason = nullableString(source['rupReason']);
    this.pesticideType = nullableString(source['pesticideType']);
    this.physicalForm = nullableString(source['physicalForm']);
    this.physicalFormName = nullableString(source['physicalFormName']);
  }

  private readPpls(source: Record<string, unknown>): void {
    this.sites = nullableString(source['sites']);
    this.pests = nullableString(source['pests']);
    this.usePattern = nullableString(source['usePattern']);
    this.transferHistory = nullableString(source['transferHistory']);
    this.epaAltBrandNames = nullableString(source['epaAltBrandNames']);
    this.meTooFlag = nullableBoolean(source['meTooFlag']);
    this.meTooRefs = nullableString(source['meTooRefs']);
    this.maxLabelDate = nullableString(source['maxLabelDate']);
    this.labelDates = nullableString(source['labelDates']);
    this.labelNames = nullableString(source['labelNames']);
    this.pmEmail = nullableString(source['pmEmail']);
    this.epaTeam = nullableString(source['epaTeam']);
    this.epaPpisSyncedAt = nullableString(source['epaPpisSyncedAt']);
    this.epaPplsSyncedAt = nullableString(source['epaPplsSyncedAt']);
  }
}
